import apiClient, { shouldUseAPI, handleAPIError } from './apiClient';

const incidentsService = {
  // Crear nuevo incidente
  async createIncident(incidentData) {
    // Si USE_API=false, simular creación exitosa con datos mock
    if (!shouldUseAPI()) {
      console.log('Modo demo: simulando creación de incidente');
      const mockIncidentId = Math.floor(Math.random() * 1000) + 200;
      return { 
        id: mockIncidentId,
        state: 'NEW',
        priority: 'P1',
        area: 'Urgencias 140',
        created_at: new Date().toISOString()
      };
    }

    // Si USE_API=true, intentar crear en la API
    try {
      // Mapear los datos del frontend al formato del backend
      const taskData = {
        // Campos requeridos del modelo Task
        contact: incidentData.contact_id,
        title: incidentData.title || `${incidentData.category_name || ''}`.trim() || 'Sin asunto',
        description: incidentData.description || 'Sin descripción',
        internal_comments: '',
        
        // Nuevos campos según el documento de especificación
        form_id: incidentData.form_id,
        category_id: incidentData.category_id,
        values: incidentData.values || [],
        
        // Mantener compatibilidad con el formato anterior
        custom_fields: incidentData.custom_fields || {},
        
        // Asignar responsable si se proporciona
        assigned_to: incidentData.assigned_to || null,
        
        // created_by y status se establecen automáticamente en el backend
      };
      
      // Guardar referencia al schema para extraer priority después
      const fieldsSchema = incidentData.fieldsSchema;
      
      // Canal por defecto: web
      const channelId = incidentData.canal_codigo || incidentData.channel_id || 'web';
      taskData.canal_codigo = String(channelId).trim();

      if (!taskData.category_id) {
        throw new Error('Debe seleccionar una categoria hoja.');
      }
      if (!taskData.form_id) {
        throw new Error('Debe seleccionar un formulario.');
      }
      
      // Extraer priority de custom_fields si existe
      // La prioridad puede venir como un campo personalizado en custom_fields
      let priorityFromCustomFields = null;
      let priorityFieldId = null;
      
      if (fieldsSchema && fieldsSchema.custom_fields && taskData.custom_fields) {
        fieldsSchema.custom_fields.forEach(({field}) => {
          const fieldName = (field.name || '').toLowerCase();
          const fieldId = field.id;
          
          if ((fieldName.includes('priority') || fieldName.includes('prioridad')) && taskData.custom_fields[fieldId]) {
            const value = taskData.custom_fields[fieldId];
            const valueStr = String(value).toUpperCase().trim();
            if (['P1', 'P2', 'P3', 'P4'].includes(valueStr)) {
              priorityFromCustomFields = valueStr;
              priorityFieldId = fieldId;
            }
          }
        });
      }
      
      // También buscar en fields del schema (formato alternativo)
      if (!priorityFromCustomFields && fieldsSchema && fieldsSchema.fields && taskData.custom_fields) {
        fieldsSchema.fields.forEach((field) => {
          const fieldName = (field.label || field.name || field.id || '').toLowerCase();
          const fieldId = field.id;
          
          // Buscar campo que contenga "priority" o "prioridad" en el nombre
          if ((fieldName.includes('priority') || fieldName.includes('prioridad')) && taskData.custom_fields[fieldId]) {
            const value = taskData.custom_fields[fieldId];
            const valueStr = String(value).toUpperCase().trim();
            if (['P1', 'P2', 'P3', 'P4'].includes(valueStr)) {
              priorityFromCustomFields = valueStr;
              priorityFieldId = fieldId;
            }
          }
        });
      }
      
      // Si no se encontró por nombre, buscar por valor en todos los custom_fields
      if (!priorityFromCustomFields && taskData.custom_fields && typeof taskData.custom_fields === 'object') {
        for (const [fieldId, value] of Object.entries(taskData.custom_fields)) {
          const valueStr = String(value).toUpperCase().trim();
          if (valueStr && ['P1', 'P2', 'P3', 'P4'].includes(valueStr)) {
            priorityFromCustomFields = valueStr;
            priorityFieldId = fieldId;
            break;
          }
        }
      }
      
      // Si se encontró priority en custom_fields, usarla y removerla de custom_fields
      if (priorityFromCustomFields) {
        taskData.priority = priorityFromCustomFields;
        if (priorityFieldId && taskData.custom_fields) {
          delete taskData.custom_fields[priorityFieldId];
        }
      } else if (incidentData.priority) {
        // Si no está en custom_fields, usar el valor directo si existe
        taskData.priority = incidentData.priority;
      }
      
      const response = await apiClient.post('/api/tasks/', taskData);
      return response.data;
    } catch (error) {
      console.error('Error al crear incidente:', error);
      
      // Si USE_API=true, propagar error en lugar de usar mocks
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      
      throw error;
    }
  },

  // Actualizar incidente existente
  async updateIncident(id, incidentData, originalTask = null, statusId = null) {
    // Si USE_API=false, simular actualización exitosa
    if (!shouldUseAPI()) {
      console.log('Modo demo: simulando actualización de incidente');
      return { 
        id: id,
        state: 'NEW',
        priority: 'P1',
        area: 'Urgencias 140',
        created_at: new Date().toISOString()
      };
    }

    // Si USE_API=true, intentar actualizar en la API
    try {
      let taskData = {};
      
      // Si tenemos el task original, usar todos sus datos (como pidió Rodrigo)
      if (originalTask) {
        // Copiar todos los campos del task original
        taskData = {
          ...originalTask,
          // Asegurarse de que los campos de relación sean IDs, no objetos
          contact: originalTask.contact && typeof originalTask.contact === 'object' 
            ? originalTask.contact.id 
            : originalTask.contact,
          category: originalTask.category && typeof originalTask.category === 'object'
            ? originalTask.category.id
            : originalTask.category,
          form: originalTask.form && typeof originalTask.form === 'object'
            ? originalTask.form.id
            : originalTask.form,
          created_by: originalTask.created_by && typeof originalTask.created_by === 'object'
            ? originalTask.created_by.id
            : originalTask.created_by,
          assigned_to: originalTask.assigned_to && typeof originalTask.assigned_to === 'object'
            ? originalTask.assigned_to.id
            : originalTask.assigned_to,
          // Remover campos que no deben enviarse en el PUT
          id: undefined,
          created_at: undefined,
          updated_at: undefined,
          custom_fields: undefined, // Se maneja por separado
          field_values: undefined, // Se maneja por separado
        };
        
        // Si hay status modificado, actualizarlo
        if (statusId !== null && statusId !== '') {
          taskData.status = statusId;
        } else {
          // Si no se modificó el status, mantener el original
          // Asegurarse de que sea un ID, no un objeto
          if (originalTask.status) {
            if (typeof originalTask.status === 'object' && originalTask.status.id) {
              taskData.status = originalTask.status.id;
            } else {
              taskData.status = originalTask.status;
            }
          }
        }
        
        // Si hay título o descripción en incidentData, actualizarlos
        if (incidentData && incidentData.title !== undefined) {
          taskData.title = incidentData.title;
        }
        if (incidentData && incidentData.description !== undefined) {
          taskData.description = incidentData.description;
        }
        
        // Si hay custom_fields en el original, incluirlos
        if (originalTask.custom_fields && Array.isArray(originalTask.custom_fields)) {
          const customFieldsObj = {};
          originalTask.custom_fields.forEach(fv => {
            if (fv.field && fv.field.id) {
              customFieldsObj[fv.field.id] = fv.value;
            }
          });
          taskData.custom_fields = customFieldsObj;
        }
      } else {
        // Si no hay task original, usar el método anterior (para compatibilidad)
        taskData = {
          // Campos requeridos del modelo Task
          contact: incidentData.contact_id,
          title: `${incidentData.category_name || ''}`.trim() || 'Sin asunto',
          description: incidentData.comments || 'Sin descripción',
          internal_comments: '',
          
          // Nuevos campos según el documento de especificación
          form_id: incidentData.form_id,
          category_id: incidentData.category_id,
          canal_codigo: incidentData.canal_codigo || incidentData.channel_id || 'web',
          values: incidentData.values || [],
          
          // Mantener compatibilidad con el formato anterior
          custom_fields: incidentData.custom_fields || {},
        };

        if (!taskData.category_id || !taskData.form_id) {
          throw new Error('Categoria y formulario son obligatorios para actualizar.');
        }
        
        // Agregar status si se proporciona
        if (statusId !== null) {
          taskData.status = statusId;
        }
      }

      // Limpiar campos undefined
      Object.keys(taskData).forEach(key => {
        if (taskData[key] === undefined) {
          delete taskData[key];
        }
      });

      const response = await apiClient.put(`/api/tasks/${id}/`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar incidente:', error);
      
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      
      throw error;
    }
  },

  // Obtener detalle de incidente por ID
  async getIncidentById(id) {
    // Si USE_API=false, retornar datos mock
    if (!shouldUseAPI()) {
      console.log('Modo demo: usando datos mock para detalle de incidente');
      return {
        id: id,
        state: 'NEW',
        priority: 'P1',
        area: 'Urgencias 140',
        classification: {
          category: 'Emergencia',
          form: 'Formulario de Emergencia'
        },
        channel: '140',
        contact: {
          id: 1,
          first_name: 'ACME S.A.',
          last_name: '',
          cuil: '30123456789',
          email: 'contacto@acme.com',
          phone: '011-1234-5678',
          phone2: '011-5555-1111',
          address: 'Parque Industrial 1200'
        },
        comments: 'Accidente reportado por conductor',
        created_at: new Date().toISOString()
      };
    }
    
    // Si USE_API=true, intentar cargar desde la API
    try {
      const response = await apiClient.get(`/api/tasks/${id}/`);
      const task = response.data;
      
      // Transformar la respuesta de Task al formato esperado por IncidentDetail
      const contactIsObject = task.contact && typeof task.contact === 'object' && !Number.isInteger(task.contact);
      const categoryIsObject = task.category && typeof task.category === 'object' && !Number.isInteger(task.category);
      const formIsObject = task.form && typeof task.form === 'object' && !Number.isInteger(task.form);
      const assignedToIsObject = task.assigned_to && typeof task.assigned_to === 'object' && !Number.isInteger(task.assigned_to);
      
      // Cargar datos relacionados solo si son IDs
      const promises = [];
      if (!contactIsObject && task.contact) {
        promises.push(apiClient.get(`/api/contacts/${task.contact}/`).then(r => ({ type: 'contact', data: r.data })).catch(() => ({ type: 'contact', data: null })));
      } else {
        promises.push(Promise.resolve({ type: 'contact', data: contactIsObject ? task.contact : null }));
      }
      
      if (!categoryIsObject && task.category) {
        promises.push(apiClient.get(`/api/categories/${task.category}/`).then(r => ({ type: 'category', data: r.data })).catch(() => ({ type: 'category', data: null })));
      } else {
        promises.push(Promise.resolve({ type: 'category', data: categoryIsObject ? task.category : null }));
      }

      if (!formIsObject && task.form) {
        promises.push(apiClient.get(`/api/forms/${task.form}/`).then(r => ({ type: 'form', data: r.data })).catch(() => ({ type: 'form', data: null })));
      } else {
        promises.push(Promise.resolve({ type: 'form', data: formIsObject ? task.form : null }));
      }
      
      if (!assignedToIsObject && task.assigned_to) {
        promises.push(apiClient.get(`/api/users/${task.assigned_to}/`).then(r => ({ type: 'assigned_to', data: r.data })).catch(() => ({ type: 'assigned_to', data: null })));
      } else {
        promises.push(Promise.resolve({ type: 'assigned_to', data: assignedToIsObject ? task.assigned_to : null }));
      }
      
      const results = await Promise.all(promises);
      const contactResult = results.find(r => r.type === 'contact');
      const categoryResult = results.find(r => r.type === 'category');
      const formResult = results.find(r => r.type === 'form');
      const assignedToResult = results.find(r => r.type === 'assigned_to');
      
      const contact = contactResult?.data || null;
      const category = categoryResult?.data || null;
      const form = formResult?.data || null;
      const assignedTo = assignedToResult?.data || null;
      
      // Obtener status completo con nombre y colores - el backend devuelve status como objeto completo
      // Mapeo estático de IDs conocidos (1: Nuevo, 2: Test Status) - solo como fallback
      const statusIdToNameMap = {
        1: 'Nuevo',
        2: 'Test Status'
      };
      
      let statusName = 'Nuevo'; // Valor por defecto
      let statusColor = null;
      let statusBackgroundColor = null;
      
      if (task.status) {
        const statusIsObject = typeof task.status === 'object' && !Number.isInteger(task.status);
        if (statusIsObject && task.status.name) {
          // Si viene como objeto completo con nombre y colores, usar todos los datos
          statusName = task.status.name;
          
          // Extraer color del texto - intentar múltiples formatos
          const colorValue = task.status.color || task.status.Color || null;
          statusColor = (colorValue && colorValue.trim() !== '') ? colorValue : null;
          
          // Extraer color de fondo - intentar múltiples formatos (snake_case y camelCase)
          const bgColorValue = task.status.background_color || task.status.backgroundColor || null;
          statusBackgroundColor = (bgColorValue && bgColorValue.trim() !== '') ? bgColorValue : null;
        } else if (typeof task.status === 'number' || (typeof task.status === 'string' && !isNaN(task.status))) {
          // Si viene como ID (número o string numérico), usar el mapeo (fallback)
          const statusId = parseInt(task.status);
          statusName = statusIdToNameMap[statusId] || `Estado ${statusId}`;
        }
      }
      
      
      // Obtener priority - primero intentar desde task.priority directamente
      let priority = null;
      
      // La prioridad viene directamente en el campo priority del task
      if (task.priority) {
        priority = task.priority;
      } else if (task.field_values && Array.isArray(task.field_values)) {
        // Si no está en priority, buscar en field_values como fallback
        task.field_values.forEach(fv => {
          if (fv.field) {
            const fieldName = (fv.field.name || '').toLowerCase();
            if (fieldName.includes('priority')) {
              priority = fv.value;
            }
          }
        });
      }
      
      // Obtener el canal directamente desde canal_codigo del task (campo del modelo Task)
      // Manejar explícitamente string vacío, null, undefined, y valores inválidos como "No especificado"
      let channelCode = null;
      
      // Valores que NO son códigos de canal válidos
      const invalidChannelValues = ['no especificado', 'sin especificar', 'n/a', 'na', ''];
      
      // Primero intentar desde canal_codigo (campo directo del modelo)
      if (task.canal_codigo !== null && task.canal_codigo !== undefined && task.canal_codigo !== '') {
        const trimmed = String(task.canal_codigo).trim();
        const trimmedLower = trimmed.toLowerCase();
        // Solo usar si NO es un valor inválido
        if (trimmed !== '' && !invalidChannelValues.includes(trimmedLower)) {
          channelCode = trimmed;
        }
      }
      
      // Si no está en canal_codigo, intentar buscarlo en custom_fields
      if (!channelCode && task.custom_fields) {
        // Buscar cualquier campo que contenga "channel" o "canal" en su clave
        for (const [key, value] of Object.entries(task.custom_fields)) {
          const keyLower = key.toLowerCase();
          if ((keyLower.includes('channel') || keyLower.includes('canal')) && value) {
            const trimmed = String(value).trim();
            const trimmedLower = trimmed.toLowerCase();
            // Solo usar si NO es un valor inválido
            if (trimmed !== '' && !invalidChannelValues.includes(trimmedLower)) {
              channelCode = trimmed;
              break;
            }
          }
        }
      }
      
      // Si aún no está, intentar buscarlo en field_values como fallback
      if (!channelCode && task.field_values && Array.isArray(task.field_values)) {
        task.field_values.forEach(fv => {
          if (fv.field && fv.value) {
            const fieldName = (fv.field.name || '').toLowerCase();
            if ((fieldName.includes('channel') || fieldName.includes('canal')) && fv.value) {
              const trimmed = String(fv.value).trim();
              const trimmedLower = trimmed.toLowerCase();
              // Solo usar si NO es un valor inválido
              if (trimmed !== '' && !invalidChannelValues.includes(trimmedLower)) {
                channelCode = trimmed;
              }
            }
          }
        });
      }
      
      // Mapeo estático de códigos de canal a nombres
      const channelNameMap = {
        '140': 'Urgencias 140',
        'web': 'Web',
        'telefonico': 'Telefónico',
        'email': 'Email'
      };
      
      // Para el display, usar el nombre mapeado o el código
      // Si channelCode existe, buscar en el mapeo (case-insensitive) o usar el código directamente
      let channelDisplay = 'No especificado';
      if (channelCode) {
        const channelCodeLower = channelCode.toLowerCase();
        channelDisplay = channelNameMap[channelCodeLower] || channelNameMap[channelCode] || channelCode;
      }
      
      // Extraer custom fields
      // El backend devuelve custom_fields como array de FieldValueSerializer
      const customFields = [];
      
      // Intentar desde task.custom_fields (viene del serializer)
      if (task.custom_fields && Array.isArray(task.custom_fields)) {
        task.custom_fields.forEach(fv => {
          if (fv.field && fv.value !== null && fv.value !== undefined && fv.value !== '') {
            // Excluir campos que ya se muestran por separado (priority, channel, etc.)
            const fieldName = (fv.field.name || '').toLowerCase();
            const excludeFields = ['priority', 'canal', 'channel'];
            if (!excludeFields.some(excluded => fieldName.includes(excluded))) {
              customFields.push({
                id: fv.field.id,
                name: fv.field.name || 'Campo sin nombre',
                type: fv.field.type || 'text',
                value: fv.value
              });
            }
          }
        });
      }
      
      // También verificar task.field_values como fallback (por si custom_fields no está disponible)
      if (customFields.length === 0 && task.field_values && Array.isArray(task.field_values)) {
        task.field_values.forEach(fv => {
          if (fv.field && fv.value !== null && fv.value !== undefined && fv.value !== '') {
            const fieldName = (fv.field.name || '').toLowerCase();
            const excludeFields = ['priority', 'canal', 'channel'];
            if (!excludeFields.some(excluded => fieldName.includes(excluded))) {
              customFields.push({
                id: fv.field.id,
                name: fv.field.name || 'Campo sin nombre',
                type: fv.field.type || 'text',
                value: fv.value
              });
            }
          }
        });
      }
      
      // Construir respuesta transformada
      // Normalizar el nombre del estado: mantener el nombre original en lugar de convertir a mayúsculas
      const incident = {
        id: task.id,
        state: statusName, // Mantener el nombre original del estado
        stateColor: statusColor, // Color del texto del estado
        stateBackgroundColor: statusBackgroundColor, // Color de fondo del estado
        priority: priority || 'P2',
        classification: {
          category: category?.name || 'No especificado',
          form: form?.name || 'No especificado'
        },
        channel: channelDisplay,
        contact: contact ? {
          id: contact.id,
          first_name: contact.first_name || '',
          last_name: contact.last_name || '',
          cuil: contact.cuil || '',
          email: contact.email || '',
          phone: contact.phone || '',
          phone2: contact.phone2 || '',
          address: contact.address || ''
        } : null,
        assignedTo: assignedTo ? {
          id: assignedTo.id,
          username: assignedTo.username || '',
          email: assignedTo.email || '',
          firstName: assignedTo.first_name || '',
          lastName: assignedTo.last_name || '',
          fullName: `${assignedTo.first_name || ''} ${assignedTo.last_name || ''}`.trim() || assignedTo.username || 'Usuario'
        } : null,
        title: task.title || null,
        description: task.description || null,
        comments: task.description || '',
        created_at: task.created_at || new Date().toISOString(),
        customFields: customFields // Agregar custom fields
      };
      
      return incident;
    } catch (error) {
      console.error('Error al obtener incidente:', error);
      
      // Si USE_API=true, propagar error en lugar de usar mocks
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      
      throw error;
    }
  },

  // Validar datos del incidente antes del envío
  validateIncidentData(data) {
    const errors = {};

    if (!data.contact_id) {
      errors.contact_id = 'Debe seleccionar o crear un contacto';
    }

    if (!data.category_id) {
      errors.category_id = 'Debe seleccionar una categoría';
    }

    if (!data.form_id) {
      errors.form_id = 'Debe seleccionar un formulario';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Obtener comentarios de una tarea
  async getTaskComments(taskId) {
    if (!shouldUseAPI()) {
      console.log('Modo demo: simulando comentarios de tarea');
      return [
        {
          id: 'TC-12345678',
          comment: 'Este es un comentario de ejemplo',
          created_by: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ];
    }

    try {
      const response = await apiClient.get(`/api/tasks/${taskId}/comments/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener comentarios:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  // Crear un comentario en una tarea
  async createTaskComment(taskId, comment) {
    if (!shouldUseAPI()) {
      console.log('Modo demo: simulando creación de comentario');
      return {
        id: 'TC-' + Math.random().toString(36).substr(2, 8),
        comment: comment,
        created_by: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    try {
      const response = await apiClient.post(`/api/tasks/${taskId}/comments/`, {
        comment: comment
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear comentario:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  // Actualizar un comentario de una tarea
  async updateTaskComment(taskId, commentId, comment) {
    if (!shouldUseAPI()) {
      console.log('Modo demo: simulando actualización de comentario');
      return {
        id: commentId,
        comment: comment,
        task: taskId,
        created_by: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }

    try {
      const response = await apiClient.put(`/api/tasks/${taskId}/comments/${commentId}/`, {
        task: taskId,
        comment: comment
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar comentario:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  async getTaskChatMessages(taskId) {
    try {
      const response = await apiClient.get(`/api/tasks/${taskId}/chat/`);
      return response.data || [];
    } catch (error) {
      console.error('Error al obtener chat de tarea:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  async createTaskChatMessage(taskId, message) {
    try {
      const response = await apiClient.post(`/api/tasks/${taskId}/chat/`, {
        message
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear mensaje de chat:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  async getTaskAttachments(taskId) {
    try {
      const response = await apiClient.get(`/api/tasks/${taskId}/attachments/`);
      return response.data || [];
    } catch (error) {
      console.error('Error al obtener adjuntos de tarea:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  },

  async uploadTaskAttachment(taskId, file) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post(`/api/tasks/${taskId}/attachments/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error al subir adjunto de tarea:', error);
      const apiError = handleAPIError(error);
      if (apiError) {
        throw apiError;
      }
      throw error;
    }
  }
};

export default incidentsService;
